import type { PillTone } from '@/components/ui/pill'

/** Statut de compte affiché en badge (dérivé côté API, SCRUM-96). */
export type AccountStatus = 'ACTIVE' | 'OVERDUE' | 'SUSPENDED'

/** Filtre statut = statut du grand livre (le badge OVERDUE est dérivé, pas filtrable ainsi). */
export type LedgerStatusFilter = 'ACTIVE' | 'SUSPENDED'

export type InvoiceStatus = 'OPEN' | 'PARTIAL' | 'PAID' | 'OVERDUE'
export type PaymentMethod = 'CASH_PARTNER' | 'BANK_TRANSFER_D17'

export interface RecoveryDriverIdentity {
  id: string
  firstName: string
  lastName: string
  phone: string | null
}

export interface RecoveryLastInvoice {
  id: string
  status: InvoiceStatus
  totalAmount: number
  dueDate: string
  periodEnd: string
}

export interface RecoveryDriver {
  driver: RecoveryDriverIdentity
  balanceDue: number
  accountStatus: AccountStatus
  lastInvoice: RecoveryLastInvoice | null
  overdueAmount: number
}

export interface RecoveryListResponse {
  data: RecoveryDriver[]
  total: number
  page: number
  limit: number
}

/** Filtres bruts lus depuis l'URL (tous optionnels hormis pagination). */
export interface RecoveryFilters {
  status?: LedgerStatusFilter
  overdue?: boolean
  page: number
  limit: number
}

/** Détail d'une facture dans la fiche chauffeur (`/admin/billing/drivers/:id`). */
export interface DriverInvoice {
  id: string
  periodStart: string
  periodEnd: string
  totalAmount: number
  paidAmount: number
  status: InvoiceStatus
  dueDate: string
  createdAt: string
}

export interface DriverPayment {
  id: string
  amount: number
  method: PaymentMethod
  reference: string | null
  proofUrl: string | null
  recordedBy: string
  createdAt: string
}

export interface DriverBilling {
  driverId: string
  balanceDue: number
  status: LedgerStatusFilter
  suspendedAt: string | null
  invoices: DriverInvoice[]
  payments: DriverPayment[]
}

export const PAGE_SIZES = [10, 25, 50] as const

/** Libellé + tonalité de pastille par statut de compte. */
export const ACCOUNT_STATUS_META: Record<AccountStatus, { label: string; tone: PillTone }> = {
  ACTIVE: { label: 'ACTIVE', tone: 'success' },
  OVERDUE: { label: 'OVERDUE', tone: 'danger' },
  SUSPENDED: { label: 'SUSPENDED', tone: 'dark' },
}

/** Options du sélecteur de statut de compte. */
export const STATUS_OPTIONS: { value: LedgerStatusFilter; label: string }[] = [
  { value: 'ACTIVE', label: 'Actifs' },
  { value: 'SUSPENDED', label: 'Suspendus' },
]

/** Libellé + tonalité par statut de facture (fiche chauffeur). */
export const INVOICE_STATUS_META: Record<InvoiceStatus, { label: string; tone: PillTone }> = {
  OPEN: { label: 'Ouverte', tone: 'neutral' },
  PARTIAL: { label: 'Partielle', tone: 'primary' },
  PAID: { label: 'Payée', tone: 'success' },
  OVERDUE: { label: 'En retard', tone: 'danger' },
}

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  CASH_PARTNER: 'Espèces (partenaire)',
  BANK_TRANSFER_D17: 'Virement / D17',
}

/** Méthodes pour lesquelles un justificatif est obligatoire (règle API SCRUM-95). */
export const METHODS_REQUIRING_PROOF: PaymentMethod[] = ['BANK_TRANSFER_D17']
